package diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme

import androidx.compose.runtime.Composable
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.Res
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.material_symbols_filled
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.material_symbols_outlined
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.public_sans
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.resources.vazirmatn
import org.jetbrains.compose.resources.Font

/**
 * Public Sans — the Directory & Community System body/heading font (matches Stitch). Loaded from the
 * bundled variable TTF, registered at the weights the type scale uses. Used for Latin scripts (EN/FR).
 */
@Composable
fun publicSansFamily(): FontFamily = FontFamily(
    Font(Res.font.public_sans, FontWeight.Normal),
    Font(Res.font.public_sans, FontWeight.Medium),
    Font(Res.font.public_sans, FontWeight.SemiBold),
    Font(Res.font.public_sans, FontWeight.Bold),
)

/**
 * Vazirmatn — the Persian/Arabic-script body/heading font used when the active language is Farsi. Public
 * Sans has no Arabic glyphs, so Farsi text falls back to the system font and looks inconsistent; swapping
 * the whole type scale to Vazirmatn keeps FA typography crisp and on-brand. Bundled variable TTF.
 */
@Composable
fun vazirmatnFamily(): FontFamily = FontFamily(
    Font(Res.font.vazirmatn, FontWeight.Normal),
    Font(Res.font.vazirmatn, FontWeight.Medium),
    Font(Res.font.vazirmatn, FontWeight.SemiBold),
    Font(Res.font.vazirmatn, FontWeight.Bold),
)

/**
 * The text family for a given language — Vazirmatn for Farsi, Public Sans for Latin scripts. Use when
 * rendering text in a language OTHER than the active theme language (e.g. the onboarding language
 * cards show every endonym in its own script): Skia (web) has no per-glyph system fallback, so Farsi
 * glyphs inside the Public Sans theme render as tofu unless given this family explicitly.
 */
@Composable
fun fontFamilyFor(language: diana.soleil.design.iraniancanadienbazzar.core.model.Language): FontFamily =
    if (language == diana.soleil.design.iraniancanadienbazzar.core.model.Language.FA) vazirmatnFamily() else publicSansFamily()

/**
 * Material Symbols Outlined — the Directory & Community System icon font (matches Stitch). Render icons
 * by ligature name via [AppIcon]; see docs/DESIGN_SYSTEM.md. Both symbol files are **static instances**
 * of the Google variable font pinned at wght 400 / GRAD 0 / opsz 24 (10.4 MB → 1.4 MB each; regenerate
 * with fontTools `instantiateVariableFont` if the icon set is ever refreshed).
 */
@Composable
fun materialSymbolsFamily(): FontFamily = FontFamily(
    Font(Res.font.material_symbols_outlined),
)

/** Material Symbols pinned at FILL=1 — Stitch's `font-variation-settings: 'FILL' 1` filled icons. */
@Composable
fun materialSymbolsFilledFamily(): FontFamily = FontFamily(
    Font(Res.font.material_symbols_filled),
)
